import React, { useCallback, useMemo } from 'react';
import CalendarUtils from 'services/CalendarUtils';
import moment from 'moment-timezone';
import { TimePicker } from 'antd';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import { Controller } from 'react-hook-form';

export const MilitaryTimePicker = (props) => {
  const value = useMemo(() => {
    if (props.value === undefined) return;
    const timezone =
      props.convertToTargetTimezone ?? true
        ? CalendarUtils.getTargetTimezone()
        : CalendarUtils.getCurrentTimezone();
    const unix = CalendarUtils.militaryTimeToUnix(moment().unix(), props.value, timezone);
    return unix;
  }, [props.value, props.convertToTargetTimezone]);

  const onChange = useCallback(
    (e, time) => {
      // const timezone = (props.convertToTargetTimezone ?? true) ? CalendarUtils.getTargetTimezone() : CalendarUtils.getCurrentTimezone()
      const militaryTime = CalendarUtils.getMilitaryTime(time);
      props.onChange?.(e, militaryTime);
    },
    [props.onChange],
  );

  return <DefaultTimePicker {...{ ...props, value, onChange }} />;
};

const DefaultTimePicker = ({
  control,
  name = undefined,
  value,
  onChange,
  label,
  disabled,
  style = {},
  small,
  field,
  id,
  minuteStep = 15,
  className,
  hideError = false,
  convertToTargetTimezone = true,
  format = 'h:mm A',
  use12Hours = true,
  disabledTime,
  hideDisabledOptions = false,
}) => {
  // RHF mode: when control + name are provided, wrap in Controller
  if (control && name) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: rhfField, fieldState }) => {
          const timePickerProps = {
            id: name,
            value: rhfField.value,
            onChange: (_e, unix) => rhfField.onChange(unix),
            label,
            disabled,
            small,
            className,
            minuteStep,
            field: {
              id: name,
              input: {
                value: rhfField.value,
                onChange: (unix) => rhfField.onChange(unix),
              },
              meta: {
                error: fieldState.error?.message,
              },
              label,
              disabled,
              small,
              className,
              minuteStep,
            },
            style,
            hideError,
            convertToTargetTimezone,
            format,
            use12Hours,
            disabledTime,
            hideDisabledOptions,
          };
          return <DefaultTimePicker {...timePickerProps} />;
        }}
      />
    );
  }

  function onChangeWrapper(time) {
    let unix = time;
    if (convertToTargetTimezone) {
      let datetime = CalendarUtils.convertToTargetTimezone(time);
      unix = datetime;
    }
    unix = unix.unix();
    if (field) field.input.onChange(unix);
    if (onChange) onChange({}, unix);
  }

  const formattedValue = useMemo(() => {
    let converted = value || (field ? field.input.value : undefined);
    if (!converted) return null;
    converted = moment.unix(converted);
    if (convertToTargetTimezone) {
      converted = CalendarUtils.convertFromTargetTimezone(converted.utc());
    }
    return converted;
  }, [field, value]);

  let chosenId = (field || {}).id || id || 'time-field';
  let chosenStyle = (field || {}).style || style;
  let chosenMinuteStep = (field || {}).minuteStep || minuteStep;
  let chosenLabel = (field || {}).label || label;
  let chosenDisabled = (field || {}).disabled || disabled;
  let chosenSmall = (field || {}).small || small;
  let chosenClassName = (field || {}).className || className;

  let pickerClassName = 'default-time-picker';
  if (chosenSmall) pickerClassName += ' small';
  if (chosenClassName) pickerClassName += ' ' + chosenClassName;

  let error = field?.meta?.error;

  return (
    <FormControl focused={true} className={pickerClassName} style={chosenStyle} error={!!error}>
      {!!chosenLabel && !chosenSmall && <InputLabel>{chosenLabel}</InputLabel>}
      <TimePicker
        id={chosenId}
        allowClear={false}
        disabled={chosenDisabled}
        bordered={false}
        defaultValue={formattedValue}
        value={formattedValue}
        minuteStep={chosenMinuteStep}
        use12Hours={use12Hours}
        format={format}
        onSelect={onChangeWrapper}
        onChange={onChangeWrapper}
        name={name}
        disabledTime={disabledTime}
        hideDisabledOptions={hideDisabledOptions}
      />
      {!!error && !hideError && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default DefaultTimePicker;
